import { useTranslation } from "react-i18next";
import { Tooltip } from '@components/new/Tooltip';
import { Text, TextColor, TextSize, TextWeight } from "@components/Text";
import styles from "./PickUpStatus.module.scss";

interface ServiceGroup {
  minWeight: number;
  minCount: number;
  weight: number;
  count: number;
  groupName: string;
  groupId: string;
}

const RestrictionMessage = ({ data }: {
  data: ServiceGroup,
}) => {
  const { t } = useTranslation();
  let template = "orderBtnMessageWithWeight";
  if (!data.minWeight && !data.minCount) return null;
  if (!data.minWeight) template = "orderBtnMessage";
  if (!data.minCount) template = "orderBtnMessageOnlyWeight";

  return (
    <Text size={TextSize.M} color={TextColor.ERROR_DARK}>
      {t(
        `pickUp.tabReady.${template}`,
        {
          serviceName: data.groupName,
          weight: (data.minWeight - data.weight) / 1000,
          count: data.minCount - data.count,
        }
      )}
    </Text>
  )
}

const PickUpStatus = ({ belowLimit, notAllowedGroups, freeGroups }: {
  belowLimit: ServiceGroup[];
  notAllowedGroups: ServiceGroup[];
  freeGroups: ServiceGroup[];
}) => {
  const { t } = useTranslation();

  if (notAllowedGroups.length >= 3) {
    return (
      <Tooltip
        action={
          <div className={styles.orderBtnMessage}>
            <Text size={TextSize.M} color={TextColor.ERROR_DARK} weight={TextWeight.BOLD}>
              {t("pickUp.tabReady.orderBtnMessageUnavailable", {
                serviceName: notAllowedGroups.slice(0, 3).map(v => v.groupName).join(", ")
              })}
            </Text>
            <Text size={TextSize.M} color={TextColor.ERROR_DARK}>
            ...
            </Text>
          </div>
        }
        text={(
          <div className={styles.orderBtnMessage}>
            <Text size={TextSize.M} color={TextColor.ERROR_DARK} weight={TextWeight.BOLD}>
              {t("pickUp.tabReady.orderBtnMessageUnavailable", { serviceName: "" })}
            </Text>
            {notAllowedGroups.map((item) => (
              <Text size={TextSize.M} color={TextColor.ERROR_DARK} weight={TextWeight.BOLD}>
                {item.groupName}
              </Text>
            ))}
          </div>
        )}
        bottom={60}
        right={-20}
        left={-20}
      />
    );
  }

  if (notAllowedGroups.length) {
    return (
      <div className={styles.orderBtnMessage}>
        <Text size={TextSize.M} color={TextColor.ERROR_DARK} weight={TextWeight.BOLD}>
          {t("pickUp.tabReady.orderBtnMessageUnavailable", {
            serviceName: notAllowedGroups.map(v => v.groupName).join(", ")
          })}
        </Text>
      </div>
    );
  }

  if (!freeGroups.length && (belowLimit.length >= 3)) {
    return (
      <Tooltip
        action={
          <div className={styles.orderBtnMessage}>
            <Text size={TextSize.M} color={TextColor.ERROR_DARK} weight={TextWeight.BOLD}>
              {t("pickUp.tabReady.orderBtnMessageTitle")}
            </Text>
            <div>
              {belowLimit.slice(0, 3).map((item, index) => (
                <>
                  {index ? (
                    <Text size={TextSize.M} color={TextColor.ERROR_DARK}>
                      &nbsp;/&nbsp;
                    </Text>
                  ) : null}
                  <RestrictionMessage data={item} key={item.groupId} />
                </>
              ))}
              <Text size={TextSize.M} color={TextColor.ERROR_DARK}>
                ...
              </Text>
            </div>
          </div>
        }
        text={(
          <div className={styles.orderBtnMessage}>
            <Text size={TextSize.M} color={TextColor.ERROR_DARK} weight={TextWeight.BOLD}>
              {t("pickUp.tabReady.orderBtnMessageTitle")}
            </Text>
            {belowLimit.map((item) => (
              <RestrictionMessage data={item} key={item.groupId} />
            ))}
          </div>
        )}
        bottom={60}
        right={-20}
        left={-20}
      />
    );
  }

  if (!freeGroups.length && belowLimit.length) {
    return (
      <div className={styles.orderBtnMessage}>
        <Text size={TextSize.M} color={TextColor.ERROR_DARK} weight={TextWeight.BOLD}>
          {t("pickUp.tabReady.orderBtnMessageTitle")}
        </Text>
        <div>
          {belowLimit.map((item, index) => (
            <>
              {index ? (
                <Text size={TextSize.M} color={TextColor.ERROR_DARK}>
                  &nbsp;/&nbsp;
                </Text>
              ) : null}
              <RestrictionMessage data={item} key={item.groupId} />
            </>
          ))}
        </div>
      </div>
    );
  }

  // free pickUp available
  return (
    <div className={styles.orderBtnMessage}>
      <Text size={TextSize.M} color={TextColor.DARK_MUTED}>
        {t("pickUp.tabReady.pickUpAvailable")}
      </Text>
    </div>
  )
};

export default PickUpStatus;
